// Types, constants, helpers, and sub-components extracted from HealthPassportPage.

import type { JSX } from "react";

export interface Trends {
  bp_trend_30d: string;
  weight_change_pct_90d: number;
  mood_decline_trend: boolean;
  fall_frequency_90d: number;
  adherence_rate_critical_meds_30d: number;
  cognitive_vs_last_visit: string;
  days_since_last_visit: number;
}

export interface PassportPatient {
  full_name: string;
  age: number;
  risk_score: number;
  risk_score_band: string;
  trends: Trends;
}

export interface VisitReport {
  _id: string;
  completed_at: string;
  overall_patient_status: string;
  escalation_flag: boolean;
  bp_systolic: number | null;
  bp_diastolic: number | null;
  heart_rate: number | null;
  spo2_percent: number | null;
  temperature_celsius: number | null;
  weight_kg: number | null;
  mood_affect: string | null;
  pain_present: boolean | null;
  pain_severity: number | null;
}

export interface Consent { p1: boolean; p2: boolean; p3: boolean; p4: boolean; }

export interface HealthPassport {
  patient: PassportPatient;
  recent_reports: VisitReport[];
  consent: Consent;
  ai_summary: string | null;
}

export const RISK_COLOR = (s: number): string =>
  s < 40 ? "text-emerald-600" : s <= 70 ? "text-amber-600" : "text-red-600";

// Keys are risk_score_band lowercased ("Green"→"green" etc.)
export const BAND_PILL: Record<string, string> = {
  green:    "bg-emerald-100 text-emerald-700",
  amber:    "bg-amber-100 text-amber-700",
  red:      "bg-red-100 text-red-700",
  critical: "bg-rose-100 text-rose-800",
};

// Keys are OverallPatientStatus values (DRS B7)
export const STATUS_PILL: Record<string, string> = {
  stable_as_expected:                "bg-green-100 text-green-700",
  minor_concerns_monitor:            "bg-emerald-100 text-emerald-700",
  noticeable_change_cs_review:       "bg-amber-100 text-amber-700",
  significant_concern_gp_recommended:"bg-orange-100 text-orange-700",
  urgent_immediate_action:           "bg-red-100 text-red-800",
};

export function fmt(v: number | null | undefined, unit = ""): string {
  return v !== null && v !== undefined ? `${v}${unit}` : "—";
}

export function trendTiles(t: Trends): { label: string; value: string }[] {
  const wc = t.weight_change_pct_90d;
  const weightVal = wc == null ? "—" : `${wc >= 0 ? "+" : ""}${wc}%`;
  return [
    { label: "BP Trend (30d)",            value: t.bp_trend_30d || "—" },
    { label: "Weight Change (90d)",        value: weightVal },
    { label: "Fall Frequency (90d)",       value: String(t.fall_frequency_90d) },
    { label: "Medication Adherence (30d)", value: `${t.adherence_rate_critical_meds_30d.toFixed(0)}%` },
    { label: "Mood Decline",               value: t.mood_decline_trend ? "Yes" : "No" },
    { label: "Cognitive vs Last Visit",    value: t.cognitive_vs_last_visit || "—" },
  ];
}

export function AiSummaryCard({ summary }: { summary: string | null }): JSX.Element | null {
  if (!summary) return null;
  return (
    <div className="bg-navy-50 border border-navy-200 rounded-xl p-5 mb-4">
      <p className="text-xs font-semibold text-navy-800 uppercase tracking-wide mb-2">AI Clinical Summary</p>
      <p className="text-sm text-navy-950 leading-relaxed">{summary}</p>
      <p className="text-xs text-navy-500 mt-2">Generated by AI — for care coordinator reference only. Not a medical diagnosis.</p>
    </div>
  );
}

export function RiskSummary({ patient }: { patient: PassportPatient }): JSX.Element {
  const t = patient.trends;
  const bandCls = BAND_PILL[patient.risk_score_band.toLowerCase()] ?? "bg-slate-100 text-slate-600";
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4 flex items-center gap-6">
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Risk Score</p>
        <p className={`text-4xl font-bold ${RISK_COLOR(patient.risk_score)}`}>{patient.risk_score}</p>
      </div>
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Risk Band</p>
        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold capitalize ${bandCls}`}>{patient.risk_score_band}</span>
      </div>
      <div className="ml-auto text-right">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Days Since Last Visit</p>
        <p className="text-2xl font-bold text-slate-800">{t.days_since_last_visit}</p>
      </div>
    </div>
  );
}

export function TrendTile({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  stable_as_expected:                "Stable",
  minor_concerns_monitor:            "Minor concerns",
  noticeable_change_cs_review:       "CS review needed",
  significant_concern_gp_recommended:"GP recommended",
  urgent_immediate_action:           "Urgent",
};

export function ReportRow({ r }: { r: VisitReport }): JSX.Element {
  const date = new Date(r.completed_at).toLocaleDateString("en-IN", { dateStyle: "short" });
  const statusKey = r.overall_patient_status;
  const pillCls = STATUS_PILL[statusKey] ?? "bg-slate-100 text-slate-600";
  const statusLabel = STATUS_LABEL[statusKey] ?? statusKey;
  const bp = r.bp_systolic !== null && r.bp_diastolic !== null
    ? `${r.bp_systolic}/${r.bp_diastolic}`
    : "—";
  return (
    <tr className="border-b border-slate-50 text-sm text-slate-700">
      <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{date}</td>
      <td className="py-2 pr-3">
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${pillCls}`}>
          {statusLabel}
        </span>
      </td>
      <td className="py-2 pr-3">{bp}</td>
      <td className="py-2 pr-3">{fmt(r.heart_rate)}</td>
      <td className="py-2 pr-3">{fmt(r.spo2_percent, "%")}</td>
      <td className="py-2 pr-3">{fmt(r.temperature_celsius, "°C")}</td>
      <td className="py-2 pr-3">{fmt(r.weight_kg, " kg")}</td>
      <td className="py-2">{r.escalation_flag ? <span title="Escalation flagged">⚠</span> : "—"}</td>
    </tr>
  );
}
